// Google Maps utility functions for SastaMastaRide

// Load Google Maps script if not already loaded
let isGoogleMapsScriptLoaded = false;
let googleMapsLoadPromise = null;

export const ensureGoogleMapsLoaded = () => {
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // Debug logging
  console.log('ensureGoogleMapsLoaded - API Key check:', apiKey ? 'Found' : 'Not found');
  
  if (!apiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set. Please add it to client/.env file and restart the React server.'));
  }

  if (isGoogleMapsScriptLoaded) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }

  isGoogleMapsScriptLoaded = true;
  
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      resolve();
    };

    script.onerror = () => {
      isGoogleMapsScriptLoaded = false;
      googleMapsLoadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

export const geocodeAddress = async (address) => {
  await ensureGoogleMapsLoaded();
  
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps API not loaded');
  }

  const geocoder = new window.google.maps.Geocoder();
  
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng(),
          formatted_address: results[0].formatted_address
        });
      } else {
        let errorMessage = `Geocoding failed: ${status}`;
        if (status === 'REQUEST_DENIED') {
          errorMessage += '. This usually means:\n' +
            '1. Geocoding API is not enabled in Google Cloud Console\n' +
            '2. API key restrictions are blocking the request\n' +
            '3. Billing is not enabled on your Google Cloud project\n' +
            'Please check: https://console.cloud.google.com/apis/library/geocoding-backend.googleapis.com';
        } else if (status === 'OVER_QUERY_LIMIT') {
          errorMessage += '. You have exceeded your API quota.';
        } else if (status === 'ZERO_RESULTS') {
          errorMessage = `No results found for "${address}".\n\nPlease:\n1. Select an address from the suggestions dropdown\n2. Or enter a more complete address (e.g., "Mumbai, Maharashtra, India")\n3. Make sure the address is spelled correctly`;
        }
        console.error('Geocoding error details:', { status, address, errorMessage });
        reject(new Error(errorMessage));
      }
    });
  });
};

export const reverseGeocode = async (lat, lng) => {
  await ensureGoogleMapsLoaded();
  
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps API not loaded');
  }

  const geocoder = new window.google.maps.Geocoder();
  const latlng = { lat: parseFloat(lat), lng: parseFloat(lng) };
  
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        resolve({
          address: results[0].formatted_address,
          lat: latlng.lat,
          lng: latlng.lng
        });
      } else {
        reject(new Error(`Reverse geocoding failed: ${status}`));
      }
    });
  });
};

export const calculateDistance = async (origin, destination) => {
  await ensureGoogleMapsLoaded();
  
  if (!window.google || !window.google.maps) {
    // Fallback calculation using Haversine formula
    const R = 6371; // Earth's radius in kilometers
    const dLat = (destination.lat - origin.lat) * Math.PI / 180;
    const dLng = (destination.lng - origin.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(origin.lat * Math.PI / 180) * Math.cos(destination.lat * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return {
      distance: R * c,
      duration: Math.round(R * c * 2) // Estimate ~2 min per km
    };
  }

  const service = new window.google.maps.DistanceMatrixService();
  
  return new Promise((resolve, reject) => {
    service.getDistanceMatrix({
      origins: [origin],
      destinations: [destination],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC,
      avoidHighways: false,
      avoidTolls: false
    }, (response, status) => {
      if (status === 'OK') {
        const result = response.rows[0].elements[0];
        resolve({
          distance: result.distance.value / 1000, // Convert to km
          duration: result.duration.value / 60 // Convert to minutes
        });
      } else {
        reject(new Error(`Distance calculation failed: ${status}`));
      }
    });
  });
};

export const getDirections = async (origin, destination) => {
  await ensureGoogleMapsLoaded();
  
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps API not loaded');
  }

  const directionsService = new window.google.maps.DirectionsService();
  
  return new Promise((resolve, reject) => {
    directionsService.route({
      origin: origin,
      destination: destination,
      travelMode: window.google.maps.TravelMode.DRIVING,
      avoidHighways: false,
      avoidTolls: false,
    }, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        reject(new Error(`Directions request failed: ${status}`));
      }
    });
  });
};

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        reject(new Error(`Geolocation error: ${error.message}`));
      },
      {
        enableHighAccuracy: true, // Use GPS for better accuracy
        timeout: 15000, // Increased timeout for better accuracy
        maximumAge: 0 // Don't use cached location, always get fresh
      }
    );
  });
};

export const watchLocation = (callback) => {
  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by this browser');
  }

  return navigator.geolocation.watchPosition(
    (position) => {
      callback({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      });
    },
    (error) => {
      console.error('Geolocation watch error:', error);
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    }
  );
};

export const stopWatchingLocation = (watchId) => {
  navigator.geolocation.clearWatch(watchId);
};

// Map styling options
export const mapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'transit',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  }
];

// Default map options
export const defaultMapOptions = {
  disableDefaultUI: false,
  clickableIcons: true,
  scrollwheel: true,
  styles: mapStyles,
  gestureHandling: 'greedy'
};
